import React from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

interface NameEmailAvatarProps {
  name?: string;
  email?: string;
  avatar: string | undefined; // URL for the avatar image or empty for initials
  avatarWidth?: string;
  avatarHeight?: string;
  nameFontSize?: string;
  emailFontSize?: string;
  centerVertically?: boolean;
  applicantStatus?: string;
}

const AVATAR_PALETTE = [
  { bg: '#dbeafe', color: '#1e40af' }, // blue
  { bg: '#f3e8ff', color: '#6b21a8' }, // purple
  { bg: '#dcfce7', color: '#166534' }, // green
  { bg: '#fce7f3', color: '#9d174d' }, // pink
  { bg: '#e0e7ff', color: '#3730a3' }, // indigo
  { bg: '#ccfbf1', color: '#115e59' }, // teal
  { bg: '#ffedd5', color: '#9a3412' }, // orange
  { bg: '#cffafe', color: '#155e75' }, // cyan
  { bg: '#d1fae5', color: '#065f46' }, // emerald
  { bg: '#ede9fe', color: '#5b21b6' }, // violet
  { bg: '#ffe4e6', color: '#9f1239' }, // rose
  { bg: '#fef3c7', color: '#92400e' }, // amber
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Outstanding':
      return { dot: '#3b82f6', bg: '#eff6ff', text: '#1e40af' };
    case 'Auto-Assessment':
      return { dot: '#a855f7', bg: '#faf5ff', text: '#6b21a8' };
    case 'Shortlisted':
      return { dot: '#10b981', bg: '#ecfdf5', text: '#065f46' };
    case 'Unsuccessful':
      return { dot: '#ef4444', bg: '#fef2f2', text: '#991b1b' };
    case 'Suitable':
      return { dot: '#06b6d4', bg: '#ecfeff', text: '#155e75' };
    case 'New':
      return { dot: '#eab308', bg: '#fefce8', text: '#854d0e' };
    case 'Completed':
      return { dot: '#059669', bg: '#ecfdf5', text: '#065f46' };
    default:
      return { dot: '#64748b', bg: '#f8fafc', text: '#334155' };
  }
};

export const NameEmailAvatar: React.FC<NameEmailAvatarProps> = ({
  name = '',
  email = '',
  avatar = '',
  avatarWidth = '36px',
  avatarHeight = '36px',
  nameFontSize = '14px',
  emailFontSize = '11px',
  centerVertically = false,
  applicantStatus,
}) => {
  const getInitials = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);
    const first = parts[0]?.charAt(0).toUpperCase() || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
    return first + last || '?';
  };

  const getColorScheme = (fullName: string) => {
    const firstLetter = fullName.trim().charAt(0).toUpperCase();
    if (!firstLetter) return AVATAR_PALETTE[0];
    const index = Math.abs((firstLetter.charCodeAt(0) - 65) % AVATAR_PALETTE.length);
    return AVATAR_PALETTE[index];
  };

  const colorScheme = getColorScheme(name);
  const statusColors = applicantStatus ? getStatusColor(applicantStatus) : null;
  const validAvatar =
    avatar &&
    avatar.trim() !== '' &&
    !avatar.includes('undefined') &&
    !avatar.includes('null');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: centerVertically ? 'center' : 'flex-start',
        flexDirection: centerVertically ? 'column' : 'row',
        textAlign: centerVertically ? 'center' : 'left',
      }}
    >
      <Avatar
        src={validAvatar ? avatar : undefined}
        alt={name}
        sx={{
          width: avatarWidth,
          height: avatarHeight,
          bgcolor: colorScheme.bg,
          color: colorScheme.color,
          fontSize: `calc(${avatarWidth} / 2.5)`,
          fontWeight: 700,
          border: '2px solid',
          borderColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          flexShrink: 0,
        }}
      >
        {getInitials(name)}
      </Avatar>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
        }}
      >
        {name && (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: nameFontSize,
              color: 'text.primary',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
        )}
        {email && (
          <Typography
            sx={{
              fontSize: emailFontSize,
              color: 'text.secondary',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email}
          </Typography>
        )}
        {applicantStatus && statusColors && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: statusColors.dot,
                    ml: '8px !important',
                    mr: '-2px !important',
                  }}
                />
              }
              label={applicantStatus}
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: statusColors.bg,
                color: statusColors.text,
                borderRadius: '9999px',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.04)',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

